/**
 * TemplateBuilder — Deep Data page for managing collection templates and fields.
 */

import { useState, useEffect, useCallback } from "react";
import {
	Table, Button, Modal, Input, Select, Switch, InputNumber,
	Space, Popconfirm, Tag, message, Divider, Form,
} from "antd";
import {
	PlusOutlined, EditOutlined, DeleteOutlined,
	ArrowUpOutlined, ArrowDownOutlined,
} from "@ant-design/icons";
import {
	getTemplates,
	getTemplateById,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	addField,
	updateField,
	deleteField,
	type CollectionTemplate,
	type CollectionTemplateWithFields,
	type CollectionTemplateField,
} from "@/services/data-collection-service";
import "./TemplateBuilder.scss";

const { TextArea } = Input;

const FIELD_TYPES = [
	{ label: "Text", value: "TEXT" },
	{ label: "Text Area", value: "TEXTAREA" },
	{ label: "Number", value: "NUMBER" },
	{ label: "Select (Single)", value: "SELECT" },
	{ label: "Multi-Select", value: "MULTISELECT" },
	{ label: "Rating (1-5)", value: "RATING" },
	{ label: "Yes / No", value: "BOOLEAN" },
	{ label: "Photo", value: "PHOTO" },
	{ label: "Currency ($)", value: "CURRENCY" },
];

const CATEGORIES = [
	"Flooring", "Roofing", "Site", "Interior", "Exterior",
	"Parking", "Landscaping", "Safety", "Plumbing", "Structural", "Other",
];

const TemplateBuilder = () => {
	const [templates, setTemplatesList] = useState<CollectionTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedTemplate, setSelectedTemplate] = useState<CollectionTemplateWithFields | null>(null);
	const [detailLoading, setDetailLoading] = useState(false);

	// Template modal
	const [tplModalOpen, setTplModalOpen] = useState(false);
	const [editingTpl, setEditingTpl] = useState<CollectionTemplate | null>(null);
	const [tplForm] = Form.useForm();

	// Field modal
	const [fieldModalOpen, setFieldModalOpen] = useState(false);
	const [editingField, setEditingField] = useState<CollectionTemplateField | null>(null);
	const [fieldForm] = Form.useForm();
	const [optionsText, setOptionsText] = useState("");

	const fetchTemplates = useCallback(async () => {
		setLoading(true);
		try {
			const t = await getTemplates(true);
			setTemplatesList(t);
		} catch { /* ignore */ }
		setLoading(false);
	}, []);

	useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

	const handleSelectTemplate = async (tpl: CollectionTemplate) => {
		setDetailLoading(true);
		try {
			const full = await getTemplateById(tpl.id);
			setSelectedTemplate(full);
		} catch {
			message.error("Failed to load template");
		}
		setDetailLoading(false);
	};

	// ─── Template CRUD ───

	const openCreateTemplate = () => {
		setEditingTpl(null);
		tplForm.resetFields();
		setTplModalOpen(true);
	};

	const openEditTemplate = (tpl: CollectionTemplate) => {
		setEditingTpl(tpl);
		tplForm.setFieldsValue({
			name: tpl.name,
			description: tpl.description,
			category: tpl.category,
			requiresPhoto: tpl.requiresPhoto,
			healthWeight: tpl.healthWeight,
		});
		setTplModalOpen(true);
	};

	const handleSaveTemplate = async () => {
		try {
			const values = await tplForm.validateFields();
			if (editingTpl) {
				await updateTemplate(editingTpl.id, values);
				message.success("Template updated");
			} else {
				await createTemplate(values);
				message.success("Template created");
			}
			setTplModalOpen(false);
			fetchTemplates();
			if (selectedTemplate && editingTpl?.id === selectedTemplate.id) {
				handleSelectTemplate(editingTpl!);
			}
		} catch { /* validation error */ }
	};

	const handleDeleteTemplate = async (id: number) => {
		try {
			await deleteTemplate(id);
			message.success("Template deleted");
			fetchTemplates();
			if (selectedTemplate?.id === id) setSelectedTemplate(null);
		} catch {
			message.error("Failed to delete template");
		}
	};

	// ─── Field CRUD ───

	const openAddField = () => {
		setEditingField(null);
		fieldForm.resetFields();
		setOptionsText("");
		setFieldModalOpen(true);
	};

	const openEditField = (field: CollectionTemplateField) => {
		setEditingField(field);
		fieldForm.setFieldsValue({
			fieldKey: field.fieldKey,
			fieldLabel: field.fieldLabel,
			fieldType: field.fieldType,
			isRequired: field.isRequired,
			aiExtractable: field.aiExtractable,
			isHealthField: field.isHealthField,
		});
		setOptionsText(field.options?.join("\n") || "");
		setFieldModalOpen(true);
	};

	const handleSaveField = async () => {
		if (!selectedTemplate) return;
		try {
			const values = await fieldForm.validateFields();
			const data: any = {
				...values,
				options: optionsText.trim() ? optionsText.split("\n").map((o: string) => o.trim()).filter(Boolean) : undefined,
				displayOrder: editingField?.displayOrder ?? (selectedTemplate.fields.length + 1),
			};

			if (editingField) {
				await updateField(selectedTemplate.id, editingField.id, data);
				message.success("Field updated");
			} else {
				await addField(selectedTemplate.id, data);
				message.success("Field added");
			}
			setFieldModalOpen(false);
			handleSelectTemplate(selectedTemplate);
		} catch { /* validation error */ }
	};

	const handleDeleteField = async (fieldId: number) => {
		if (!selectedTemplate) return;
		try {
			await deleteField(selectedTemplate.id, fieldId);
			message.success("Field deleted");
			handleSelectTemplate(selectedTemplate);
		} catch {
			message.error("Failed to delete field");
		}
	};

	// ─── Template List Columns ───

	const tplColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (v: string, r: CollectionTemplate) => (
				<Button type="link" onClick={() => handleSelectTemplate(r)}>{v}</Button>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			render: (v: string) => v ? <Tag color="purple">{v}</Tag> : "—",
		},
		{
			title: "Active",
			dataIndex: "isActive",
			key: "active",
			render: (v: boolean) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
		},
		{
			title: "Health Weight",
			dataIndex: "healthWeight",
			key: "weight",
			render: (v: number) => `${Math.round(v * 100)}%`,
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: unknown, r: CollectionTemplate) => (
				<Space size="small">
					<Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditTemplate(r)}>Edit</Button>
					<Popconfirm title="Delete this template?" onConfirm={() => handleDeleteTemplate(r.id)}>
						<Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	// ─── Field Columns ───

	const fieldColumns = [
		{
			title: "#",
			dataIndex: "displayOrder",
			key: "order",
			width: 50,
		},
		{
			title: "Key",
			dataIndex: "fieldKey",
			key: "key",
			render: (v: string) => <code>{v}</code>,
		},
		{
			title: "Label",
			dataIndex: "fieldLabel",
			key: "label",
		},
		{
			title: "Type",
			dataIndex: "fieldType",
			key: "type",
			render: (v: string) => <Tag>{v}</Tag>,
		},
		{
			title: "Required",
			dataIndex: "isRequired",
			key: "required",
			render: (v: boolean) => v ? <Tag color="red">Yes</Tag> : "No",
		},
		{
			title: "AI",
			dataIndex: "aiExtractable",
			key: "ai",
			render: (v: boolean) => v ? <Tag color="blue">AI</Tag> : "—",
		},
		{
			title: "Health",
			dataIndex: "isHealthField",
			key: "health",
			render: (v: boolean) => v ? <Tag color="green">Health</Tag> : "—",
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: unknown, r: CollectionTemplateField) => (
				<Space size="small">
					<Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditField(r)}>Edit</Button>
					<Popconfirm title="Delete this field?" onConfirm={() => handleDeleteField(r.id)}>
						<Button type="link" size="small" danger icon={<DeleteOutlined />}>Delete</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="template-builder-page">
			<div className="tb-header">
				<h2>Collection Templates</h2>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreateTemplate}>
					New Template
				</Button>
			</div>

			<Table
				dataSource={templates}
				columns={tplColumns}
				rowKey="id"
				loading={loading}
				size="small"
				pagination={false}
			/>

			{/* Selected Template Fields */}
			{selectedTemplate && (
				<div className="tb-fields-section">
					<Divider />
					<div className="tb-fields-header">
						<h3>Fields — {selectedTemplate.name}</h3>
						<Button icon={<PlusOutlined />} onClick={openAddField}>Add Field</Button>
					</div>
					{detailLoading ? (
						<div style={{ textAlign: "center", padding: 20 }}>Loading...</div>
					) : (
						<Table
							dataSource={selectedTemplate.fields}
							columns={fieldColumns}
							rowKey="id"
							size="small"
							pagination={false}
						/>
					)}
				</div>
			)}

			{/* Template Modal */}
			<Modal
				open={tplModalOpen}
				onCancel={() => setTplModalOpen(false)}
				onOk={handleSaveTemplate}
				title={editingTpl ? "Edit Template" : "New Template"}
				okText="Save"
			>
				<Form form={tplForm} layout="vertical">
					<Form.Item name="name" label="Name" rules={[{ required: true }]}>
						<Input placeholder="e.g., Carpet Assessment" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<TextArea placeholder="What data does this template collect?" rows={2} />
					</Form.Item>
					<Form.Item name="category" label="Category">
						<Select placeholder="Select category" options={CATEGORIES.map((c) => ({ label: c, value: c }))} allowClear />
					</Form.Item>
					<Form.Item name="requiresPhoto" label="Requires Photo" valuePropName="checked" initialValue={true}>
						<Switch />
					</Form.Item>
					<Form.Item name="healthWeight" label="Health Weight (0-1)" initialValue={0.5}>
						<InputNumber min={0} max={1} step={0.1} style={{ width: "100%" }} />
					</Form.Item>
				</Form>
			</Modal>

			{/* Field Modal */}
			<Modal
				open={fieldModalOpen}
				onCancel={() => setFieldModalOpen(false)}
				onOk={handleSaveField}
				title={editingField ? "Edit Field" : "Add Field"}
				okText="Save"
			>
				<Form form={fieldForm} layout="vertical">
					<Form.Item name="fieldKey" label="Field Key" rules={[{ required: true }]}>
						<Input placeholder="e.g., condition_rating" disabled={!!editingField} />
					</Form.Item>
					<Form.Item name="fieldLabel" label="Display Label" rules={[{ required: true }]}>
						<Input placeholder="e.g., Condition Rating" />
					</Form.Item>
					<Form.Item name="fieldType" label="Field Type" rules={[{ required: true }]}>
						<Select options={FIELD_TYPES} placeholder="Select type" />
					</Form.Item>
					<Form.Item name="isRequired" label="Required" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item name="aiExtractable" label="AI Extractable" valuePropName="checked" initialValue={true}>
						<Switch />
					</Form.Item>
					<Form.Item name="isHealthField" label="Health Field" valuePropName="checked">
						<Switch />
					</Form.Item>
					<Form.Item label="Options (one per line, for Select/MultiSelect)">
						<TextArea
							rows={4}
							value={optionsText}
							onChange={(e) => setOptionsText(e.target.value)}
							placeholder={"Good\nFair\nPoor\nCritical"}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default TemplateBuilder;
